import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { IFormData } from "@litres-libs/types";
import { EAuthStep, EFallbackLoginStep } from "@litres-libs/types";
import { addHydrateReducer } from "../../../utils/hydrateReducer";

interface IAuthsState {
  activeStep: EAuthStep | null;
  fallbackStep: EFallbackLoginStep;
  stepsHistory: (EAuthStep | null)[];
  formData: IFormData;
  isRateLimitError?: boolean;
}

export const initialState: IAuthsState = {
  activeStep: EAuthStep.authenticationByEmail,
  fallbackStep: EFallbackLoginStep.init,
  stepsHistory: [],
  formData: {},
  isRateLimitError: false,
};

export const authSlice = createSlice({
  name: "global/auth",
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setActiveStepHard: (
      state: IAuthsState,
      action: PayloadAction<EAuthStep>
    ) => {
      state.activeStep = action.payload;
    },
    setActiveStep: (state: IAuthsState, action: PayloadAction<EAuthStep>) => {
      state.stepsHistory.push(state.activeStep);
      state.activeStep = action.payload;
    },
    setFallbackStep: (
      state: IAuthsState,
      action: PayloadAction<EFallbackLoginStep>
    ) => {
      state.fallbackStep = action.payload;
    },
    goToPreviousStep: (state: IAuthsState) => {
      const step = state.stepsHistory.pop();
      state.activeStep = step || EAuthStep.authenticationByPhone;
    },
    setFormData: (
      state: IAuthsState,
      action: PayloadAction<Partial<IFormData>>
    ) => {
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
        },
      };
    },
    setRateLimitError: (state: IAuthsState, action: PayloadAction<boolean>) => {
      state.isRateLimitError = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "global.auth");
  },
});

export const {
  setActiveStep,
  setActiveStepHard,
  goToPreviousStep,
  setFormData,
  resetState,
  setFallbackStep,
  setRateLimitError,
} = authSlice.actions;

export default authSlice.reducer;
